"use client";
import { useState, useEffect, useMemo, useCallback, type KeyboardEvent } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useAuth } from "@/lib/auth-context";
import AdvisorAvatar from "@/components/advisor/AdvisorAvatar";
import { favorites as favoritesApi, listings as listingsApi, savedSearches as savedSearchesApi, type TownMatchesResponse } from "@/lib/api";
import { useBreakpoint } from "@/lib/hooks";
import { buildAdvisorUrl } from "@/lib/advisor-context";
import { clearRecentSearch, getRecentSearch, getRecentSearchHistory } from "@/lib/recent-search-storage";
import MayWordmark from "@/components/brand/MayWordmark";
import {
  cityFromContext,
  getHomepageAutocompleteSuggestions,
  getHomepageMarketOrder,
  getHomepageSearchRecommendations,
  getRecommendationHeading,
  MARKET_RECOMMENDATIONS,
  searchAssistMarketForTown,
  type AvailableSearchMarket,
  type BrowserLocation,
  type HomepageAutocompleteSuggestion,
  type HomepageSearchRecommendation,
  type SearchContextItem,
} from "@/lib/homepage-recommendations";
import { uniquePhotos } from "@/lib/photos";
import { normalizeSearchQueryForSubmission } from "@/lib/search-normalization";
import type { Listing } from "@/types";

const MARKET_GROUPS = Object.entries(MARKET_RECOMMENDATIONS).map(([key, market]) => ({
  key,
  label: market.label,
  description: market.description,
  cities: market.towns,
  searches: market.starterSearches,
}));

// Towns we request listing counts for — DERIVED from the curated market towns (the same
// source the cards display via group.cities), deduped + order-stable. Deriving instead of
// hand-maintaining guarantees every town shown on the "Explore towns" hub also gets a count
// (no drift), so curated towns never render "Search" just for being missing from this list.
const NJ_CITIES = Array.from(new Set(MARKET_GROUPS.flatMap(group => group.cities)));

// Three differentiators reused on /about. Lock the wording so the message is consistent everywhere.
const HOME_ADVISOR_STEPS = [
  {
    step: "01",
    title: "Understand tradeoffs",
    body: "Mae explains why a home may or may not fit: price, location, missing data, commute, schools, and tradeoffs — not just the listing facts.",
  },
  {
    step: "02",
    title: "Remember what you like",
    body: "Save, hide, and react to homes. Mae learns your preferences and keeps that context across searches.",
  },
  {
    step: "03",
    title: "Watch the market with reasoning",
    body: "Turn a search into a watch so OpenHomesAI can monitor future listings, near-matches, and price changes — and explain why each one matched.",
  },
];

interface LastCriteria {
  city?: string;
  max_price?: number;
  min_beds?: number;
  min_baths?: number;
}

interface FavoriteHomeEntry {
  listing?: Listing | null;
}

interface SearchHubItem {
  id: string;
  section: "Action" | "Resume" | "Saved" | "AI";
  label: string;
  detail: string;
  kind: string;
  onSelect: () => void;
}

function moneyLabel(value: number | null | undefined) {
  if (!value) return "Price not listed";
  return `$${Math.round(value).toLocaleString()}`;
}

function compactFacts(listing: Listing) {
  return [
    listing.beds ? `${listing.beds} bd` : null,
    listing.baths ? `${listing.baths} ba` : null,
    listing.sqft ? `${Math.round(listing.sqft).toLocaleString()} sqft` : null,
  ].filter(Boolean).join(" · ");
}

function searchContextLabel(item: SearchContextItem) {
  const criteria = item.criteria || {};
  const city = cityFromContext(item);
  const beds = typeof criteria.min_beds === "number" ? `${criteria.min_beds}+ beds` : null;
  const maxPrice = typeof criteria.max_price === "number"
    ? `under ${criteria.max_price >= 1_000_000 ? `$${(criteria.max_price / 1_000_000).toFixed(criteria.max_price % 1_000_000 === 0 ? 0 : 1)}M` : `$${Math.round(criteria.max_price / 1000)}K`}`
    : null;
  return [city, beds, maxPrice].filter(Boolean).join(" · ") || item.query || "Recent search";
}

function searchContextQuery(item: SearchContextItem) {
  const criteria = item.criteria || {};
  return item.query || [
    cityFromContext(item),
    criteria.min_beds ? `${criteria.min_beds} bed` : null,
    criteria.max_price ? `under $${criteria.max_price}` : null,
  ].filter(Boolean).join(" ");
}

function HomepageListingCard({ listing, onOpen }: { listing: Listing; onOpen: (listing: Listing) => void }) {
  const { isMobile } = useBreakpoint();
  const photo = uniquePhotos(listing.photos)[0];
  const facts = compactFacts(listing);
  const reason = listing.assistant_reason || listing.search_fit_label || listing.advisor_fit_label || (listing.city ? `Explore homes in ${listing.city}` : "Explore this listing");
  return (
    <button
      type="button"
      onClick={() => onOpen(listing)}
      style={{
        display: "grid",
        gridTemplateRows: isMobile ? "164px auto" : "150px auto",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-lg)",
        overflow: "hidden",
        background: "var(--color-surface)",
        boxShadow: "var(--shadow-card)",
        textAlign: "left",
        cursor: "pointer",
        fontFamily: "inherit",
        scrollSnapAlign: "start",
      }}
    >
      <div style={{ position: "relative", width: "calc(100% - 16px)", maxWidth: "100%", minWidth: 0, margin: "8px 8px 0", borderRadius: "var(--radius-md)", background: "var(--color-border-3)", overflow: "hidden", boxSizing: "border-box" }}>
        {photo ? (
          <Image src={photo} alt={listing.address || "Home listing"} fill sizes="(max-width: 768px) 78vw, 280px" style={{ objectFit: "cover" }} />
        ) : (
          <div style={{ width: "100%", maxWidth: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 7, background: "linear-gradient(135deg, var(--color-surface-muted), var(--color-advisor-bg))", color: "var(--color-ink)", fontSize: 14, fontWeight: 700, overflow: "hidden", boxSizing: "border-box" }}>
            <span style={{ width: 34, height: 34, borderRadius: "var(--radius-full)", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--color-surface)", border: "1px solid var(--color-border)" }}>⌂</span>
            <span style={{ maxWidth: "100%", padding: "0 10px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", boxSizing: "border-box" }}>Photo coming soon</span>
          </div>
        )}
        {(listing.display_status || listing.status) && (
          <span style={{ position: "absolute", top: 9, left: 9, borderRadius: "var(--radius-full)", background: "var(--color-glass)", color: "var(--color-ink)", padding: "4px 8px", fontSize: 12, fontWeight: 600, boxShadow: "var(--shadow-soft)" }}>
            {listing.display_status || listing.status}
          </span>
        )}
      </div>
      <div style={{ padding: isMobile ? "11px 12px 12px" : "12px 13px 13px", minWidth: 0 }}>
        <p style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: "var(--color-ink)", lineHeight: 1.1 }}>{moneyLabel(listing.price)}</p>
        {facts && <p style={{ margin: "0 0 4px", fontSize: 14, color: "var(--color-ink-soft)", fontWeight: 700 }}>{facts}</p>}
        <p style={{ margin: "0 0 9px", fontSize: 12, color: "var(--color-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {listing.address || "Address not listed"}{listing.city ? `, ${listing.city}` : ""}
        </p>
        <p style={{ margin: 0, borderTop: isMobile ? "none" : "1px solid var(--color-border-3)", paddingTop: isMobile ? 0 : 9, color: isMobile ? "var(--color-muted)" : "var(--color-advisor-text)", fontSize: 12, lineHeight: 1.35, fontWeight: 600, display: "-webkit-box", WebkitLineClamp: isMobile ? 1 : 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {reason.replace(/^Recommended because\s*/i, "")}
        </p>
      </div>
    </button>
  );
}

export default function HomePage() {
  const [query, setQuery] = useState("");
  // The hero photo (public/images/hero): ivory type over a warm scrim, glass chips.
  const heroInk = "var(--color-surface)";
  const heroMuted = "var(--color-on-dark-muted)";
  const heroEyebrow = "var(--color-glass)";
  const heroChip = { background: "var(--color-glass-dim)", border: "1px solid var(--color-glass-border)", color: "var(--color-glass)" };
  const { user } = useAuth();
  const router = useRouter();
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [lastCriteria, setLastCriteria] = useState<LastCriteria | null>(null);
  const [recentSearches, setRecentSearches] = useState<SearchContextItem[]>([]);
  const [savedSearchContexts, setSavedSearchContexts] = useState<SearchContextItem[]>([]);
  const [favoriteHomes, setFavoriteHomes] = useState<Listing[]>([]);
  const [availableMarkets, setAvailableMarkets] = useState<AvailableSearchMarket[]>([]);
  const [starterListings, setStarterListings] = useState<Listing[]>([]);
  const [starterListingsContext, setStarterListingsContext] = useState<{ town: string; nearby: boolean } | null>(null);
  const [starterListingsLoading, setStarterListingsLoading] = useState(false);
  const [browserLocation, setBrowserLocation] = useState<BrowserLocation | null>(null);
  const [locationStatus, setLocationStatus] = useState<"idle" | "requesting" | "granted" | "denied" | "unsupported">("idle");
  const [cityCounts, setCityCounts] = useState<Record<string, number>>({});
  const [selectedMarket, setSelectedMarket] = useState(0);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const { isMobile } = useBreakpoint();

  useEffect(() => {
    let cancelled = false;
    listingsApi.markets()
      .then(r => {
        if (cancelled) return;
        const markets = Array.isArray(r.data.markets) ? r.data.markets : [];
        setAvailableMarkets(markets.filter(market => market.city && market.state));
      })
      .catch(() => {
        if (!cancelled) setAvailableMarkets([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    listingsApi.counts(NJ_CITIES).then(r => {
      setTotalCount(r.data.total);
      setCityCounts(r.data.counts);
    }).catch(() => {
      listingsApi.count().then(r => setTotalCount(r.data.count)).catch(() => {});
    });

    // Load last search criteria from localStorage for resume banner
    try {
      const history = getRecentSearchHistory(user);
      const recent = history[0] || getRecentSearch(user);
      if (recent) {
        setRecentSearches(history.length ? history : [recent]);
        const c = recent.criteria;
        if (c && Object.values(c).some(v => v)) setLastCriteria(c);
      } else {
        setRecentSearches([]);
      }
    } catch {}
  }, [user]);

  useEffect(() => {
    if (!user) {
      setSavedSearchContexts([]);
      setFavoriteHomes([]);
      return;
    }
    savedSearchesApi.list()
      .then(r => {
        const contexts = (Array.isArray(r.data) ? r.data : []).map((item: { raw_query?: string | null; criteria?: Record<string, unknown> }) => ({
          query: item.raw_query || "",
          criteria: item.criteria || {},
        }));
        setSavedSearchContexts(contexts);
      })
      .catch(() => setSavedSearchContexts([]));
    favoritesApi.list()
      .then(r => {
        const homes = (Array.isArray(r.data) ? r.data : [])
          .map((item: FavoriteHomeEntry) => item.listing)
          .filter(Boolean) as Listing[];
        setFavoriteHomes(homes.slice(0, 4));
      })
      .catch(() => setFavoriteHomes([]));
  }, [user]);

  const handleSearch = useCallback((q?: string) => {
    const term = normalizeSearchQueryForSubmission(q || query);
    if (!term.trim()) return;
    setAutocompleteOpen(false);
    router.push(`/results?q=${encodeURIComponent(term.trim())}`);
  }, [query, router]);

  const selectAutocompleteSuggestion = useCallback((suggestion: HomepageAutocompleteSuggestion) => {
    setQuery(suggestion.query);
    handleSearch(suggestion.query);
  }, [handleSearch]);

  const startFresh = () => {
    try {
      clearRecentSearch(user);
    } catch {}
    setLastCriteria(null);
    setRecentSearches([]);
    setQuery("");
  };

  const requestBetterLocation = useCallback(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setLocationStatus("unsupported");
      return;
    }
    setLocationStatus("requesting");
    navigator.geolocation.getCurrentPosition(
      position => {
        setBrowserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setLocationStatus("granted");
      },
      () => setLocationStatus("denied"),
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 86_400_000 },
    );
  }, []);

  const resumeCriteria = lastCriteria;
  const resumeLabel = resumeCriteria
    ? [
        resumeCriteria.city,
        resumeCriteria.min_beds ? `${resumeCriteria.min_beds}+ bed` : null,
        resumeCriteria.max_price
          ? `under $${resumeCriteria.max_price >= 1000000 ? (resumeCriteria.max_price / 1000000).toFixed(1) + "M" : (resumeCriteria.max_price / 1000).toFixed(0) + "K"}`
          : null,
      ].filter(Boolean).join(" · ")
    : null;
  const resumeTitle = resumeCriteria?.city ? `Resume your ${resumeCriteria.city} search` : "Resume your recent search";

  const resumeQuery = resumeCriteria
    ? [
        resumeCriteria.city,
        resumeCriteria.min_beds ? `${resumeCriteria.min_beds} bed` : null,
        resumeCriteria.max_price ? `under $${resumeCriteria.max_price}` : null,
      ].filter(Boolean).join(" ")
    : "";
  const recommendations = useMemo(() => getHomepageSearchRecommendations({
    user,
    recentSearches,
    savedSearches: savedSearchContexts,
    favorites: favoriteHomes.map(home => ({ city: home.city })),
    browserLocation,
    approximateRegion: "North Jersey",
  }), [browserLocation, favoriteHomes, recentSearches, savedSearchContexts, user]);
  // The backend's town matching for what is typed (GET /listings/town-matches, the
  // same matcher the query parser uses), fetched a beat after typing stops. Only a
  // response for the text still in the box is used.
  const [townMatches, setTownMatches] = useState<TownMatchesResponse | null>(null);
  useEffect(() => {
    const typed = query.trim();
    if (typed.length < 2) {
      setTownMatches(null);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      listingsApi.townMatches(typed)
        .then(response => { if (!cancelled) setTownMatches(response.data); })
        .catch(() => { if (!cancelled) setTownMatches(null); });
    }, 120);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);
  const autocompleteSuggestions = useMemo(() => getHomepageAutocompleteSuggestions({
    user,
    query,
    recentSearches,
    savedSearches: savedSearchContexts,
    browserLocation,
    approximateRegion: "North Jersey",
    availableMarkets,
    townMatches,
  }), [availableMarkets, browserLocation, query, recentSearches, savedSearchContexts, townMatches, user]);
  const recommendationHeading = getRecommendationHeading(recommendations);
  const anchorTown = useMemo(() => (
    cityFromContext(recentSearches[0])
    || cityFromContext(savedSearchContexts[0])
    || favoriteHomes[0]?.city
    || "Morristown"
  ), [favoriteHomes, recentSearches, savedSearchContexts]);
  const marketOrder = useMemo(() => getHomepageMarketOrder({
    user,
    recentSearches,
    savedSearches: savedSearchContexts,
    favorites: favoriteHomes.map(home => ({ city: home.city })),
    browserLocation,
    approximateRegion: "North Jersey",
  }), [browserLocation, favoriteHomes, recentSearches, savedSearchContexts, user]);
  const visibleMarkets = useMemo(() => {
    const orderedMarkets = marketOrder
    .map(key => MARKET_GROUPS.find(group => group.key === key))
    .filter(Boolean) as typeof MARKET_GROUPS;
    return orderedMarkets.length ? orderedMarkets : MARKET_GROUPS;
  }, [marketOrder]);
  const activeMarket = visibleMarkets[selectedMarket] || visibleMarkets[0];
  const activeMarketTotal = activeMarket.cities.reduce((sum, city) => sum + (cityCounts[city] || 0), 0);
  const mobileSearchIdeas = useMemo(() => {
    const towns = Array.from(new Set([
      anchorTown,
      "Morristown",
      "Madison",
      "Chatham",
      "Mendham",
    ].filter(Boolean))).slice(0, 4) as string[];
    return [
      ...towns.map(town => ({
        label: town,
        onSelect: () => handleSearch(`${town}, NJ`),
      })),
      {
        label: "Use location",
        onSelect: requestBetterLocation,
      },
    ];
  }, [anchorTown, handleSearch, requestBetterLocation]);
  const pageFont = "var(--font-sans)";
  const activeMarketFirstCity = activeMarket?.cities[0];
  const showAutocompleteResults = query.trim().length >= 2;
  const searchHubItems = useMemo<SearchHubItem[]>(() => {
    const items: SearchHubItem[] = [];
    items.push({
      id: "current-location",
      section: "Action",
      label: locationStatus === "granted" ? "Use searches near me" : "Use current location",
      detail: locationStatus === "granted" ? "Starter searches are tuned to your area." : "Improve starter searches without making location required.",
      kind: "location",
      onSelect: requestBetterLocation,
    });
    recentSearches.slice(0, 4).forEach((item, index) => {
      const searchQuery = searchContextQuery(item);
      if (!searchQuery) return;
      items.push({
        id: `recent-${index}-${searchQuery}`,
        section: "Resume",
        label: index === 0 && cityFromContext(item) ? `Resume your ${cityFromContext(item)} search` : searchContextLabel(item),
        detail: searchContextLabel(item),
        kind: "history",
        onSelect: () => handleSearch(searchQuery),
      });
    });
    if (user) {
      items.push({
        id: "saved-searches",
        section: "Saved",
        label: "Saved searches",
        detail: savedSearchContexts.length ? `${savedSearchContexts.length} saved search${savedSearchContexts.length === 1 ? "" : "es"}` : "Review searches you are watching.",
        kind: "saved",
        onSelect: () => router.push("/searches"),
      });
      items.push({
        id: "saved-homes",
        section: "Saved",
        label: "Saved homes",
        detail: favoriteHomes.length ? `${favoriteHomes.length} saved home${favoriteHomes.length === 1 ? "" : "s"}` : "Open your shortlist.",
        kind: "homes",
        onSelect: () => router.push("/searches#saved-homes"),
      });
      favoriteHomes.slice(0, 2).forEach(home => {
        items.push({
          id: `favorite-${home.id}`,
          section: "Saved",
          label: home.address || `${home.city || "Saved"} home`,
          detail: `${moneyLabel(home.price)}${home.city ? ` · ${home.city}` : ""}`,
          kind: "home",
          onSelect: () => router.push(`/listing/${home.id}`),
        });
      });
    }
    items.push({
      id: "ai-search",
      section: "AI",
      label: "Search smarter with AI",
      detail: "Describe your ideal home, budget, must-haves, and tradeoffs.",
      kind: "AI",
      onSelect: () => {
        const prompt = anchorTown ? `${anchorTown} homes with privacy, practical layout, and good tradeoffs` : "3 bed home near Morristown under $900K with privacy";
        setQuery(prompt);
        setAutocompleteOpen(false);
        handleSearch(prompt);
      },
    });
    return items;
  }, [anchorTown, favoriteHomes, handleSearch, locationStatus, recentSearches, requestBetterLocation, router, savedSearchContexts.length, user]);
  const activeDropdownCount = showAutocompleteResults ? autocompleteSuggestions.length : searchHubItems.length;

  useEffect(() => {
    const city = anchorTown || activeMarketFirstCity || "Morristown";
    const anchorMarket = searchAssistMarketForTown(city);
    const candidateCities = Array.from(new Set([city, ...(anchorMarket?.nearbyMarkets || [])].filter(Boolean)));
    const hasPhoto = (item: Listing) => Boolean(uniquePhotos(item.photos)[0]);
    // Hide photo-less listings from the homepage carousel — they read as broken on first impression.
    // If filtering would leave us with too few cards, fall back to the unfiltered set so the section stays populated.
    const preferPhotos = (items: Listing[], minRemaining: number) => {
      const withPhotos = items.filter(hasPhoto);
      return withPhotos.length >= minRemaining ? withPhotos : items;
    };
    const sortListings = (items: Listing[]) => [...items].sort((a, b) => Number(hasPhoto(b)) - Number(hasPhoto(a)));
    const minVisible = isMobile ? 3 : 4;
    let cancelled = false;
    setStarterListingsLoading(true);
    (async () => {
      try {
        for (const candidate of candidateCities) {
          // Pull a wider page so the photo filter can drop weak rows without leaving the section empty.
          const r = await listingsApi.search({ city: candidate, limit: isMobile ? 8 : 12, sort_by: "newest" });
          const data = Array.isArray(r.data) ? preferPhotos(sortListings(r.data), minVisible) : [];
          if (data.length > 0) {
            if (!cancelled) {
              setStarterListings(data);
              setStarterListingsContext({ town: candidate, nearby: candidate.toLowerCase() !== city.toLowerCase() });
            }
            return;
          }
        }
        const fallback = await listingsApi.search({ limit: isMobile ? 8 : 12, sort_by: "newest" });
        if (!cancelled) {
          setStarterListings(preferPhotos(sortListings(Array.isArray(fallback.data) ? fallback.data : []), minVisible));
          setStarterListingsContext({ town: city, nearby: true });
        }
      } catch {
        if (!cancelled) {
          setStarterListings([]);
          setStarterListingsContext(null);
        }
      } finally {
        if (!cancelled) setStarterListingsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeMarketFirstCity, anchorTown, isMobile]);

  useEffect(() => {
    if (selectedMarket >= visibleMarkets.length) setSelectedMarket(0);
  }, [selectedMarket, visibleMarkets.length]);

  useEffect(() => {
    setActiveSuggestionIndex(-1);
  }, [query, activeDropdownCount]);

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" && activeDropdownCount) {
      e.preventDefault();
      setAutocompleteOpen(true);
      setActiveSuggestionIndex(index => index < 0 ? 0 : Math.min(index + 1, activeDropdownCount - 1));
      return;
    }
    if (e.key === "ArrowUp" && activeDropdownCount) {
      e.preventDefault();
      setAutocompleteOpen(true);
      setActiveSuggestionIndex(index => index < 0 ? activeDropdownCount - 1 : Math.max(index - 1, 0));
      return;
    }
    if (e.key === "Escape") {
      setAutocompleteOpen(false);
      return;
    }
    if (e.key === "Enter") {
      if (autocompleteOpen && activeSuggestionIndex >= 0 && showAutocompleteResults && autocompleteSuggestions[activeSuggestionIndex]) {
        e.preventDefault();
        selectAutocompleteSuggestion(autocompleteSuggestions[activeSuggestionIndex]);
        return;
      }
      if (autocompleteOpen && activeSuggestionIndex >= 0 && !showAutocompleteResults && searchHubItems[activeSuggestionIndex]) {
        e.preventDefault();
        searchHubItems[activeSuggestionIndex].onSelect();
        return;
      }
      e.preventDefault();
      handleSearch();
    }
  };

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    console.info("Homepage search recommendation context", {
      detectedRegion: activeMarket?.label,
      recommendationSource: recommendations[0]?.source,
      recentSearchUsed: recentSearches[0]?.query || recentSearches[0]?.criteria || null,
      generatedStarterSearches: recommendations.map(item => item.query),
      selectedMarketBoardOrdering: visibleMarkets.map(market => market.label),
    });
  }, [activeMarket?.label, recommendations, recentSearches, visibleMarkets]);

  return (
    <div style={{ minHeight: "calc(var(--app-viewport-height) - 56px)", background: "var(--color-bg)", color: "var(--color-ink)", fontFamily: pageFont, overflowX: "hidden", paddingBottom: isMobile ? "calc(76px + env(safe-area-inset-bottom))" : undefined }}>

      {resumeLabel && (
        <div style={{ background: "var(--color-surface)", borderBottom: "1px solid var(--color-border)", boxShadow: "var(--shadow-raised)", padding: isMobile ? "8px 14px" : "9px 28px", display: "flex", alignItems: isMobile ? "center" : "center", justifyContent: "space-between", flexWrap: isMobile ? "nowrap" : "wrap", gap: isMobile ? 8 : 10, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 7, height: 7, borderRadius: "var(--radius-full)", background: "var(--color-ink)", display: "inline-block", flexShrink: 0, boxShadow: "0 0 0 4px var(--color-focus-ring)" }} />
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 14, color: "var(--color-ink)", fontWeight: 700, margin: 0 }}>
                {resumeTitle}
              </p>
              <p style={{ fontSize: 12, color: "var(--color-muted)", fontWeight: 600, margin: "1px 0 0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{resumeLabel}</p>
            </div>
          </div>
          <div style={{ display: "flex", gap: isMobile ? 6 : 10, alignItems: "center", width: "auto", flexShrink: 0 }}>
            <button onClick={() => handleSearch(resumeQuery)}
              className="oh-button-primary"
              style={{ fontSize: 14, fontWeight: 700, borderRadius: "var(--radius-full)", padding: isMobile ? "7px 11px" : "7px 12px", cursor: "pointer", fontFamily: "inherit", flex: isMobile ? "0 0 auto" : undefined }}>
              Resume
            </button>
            <button onClick={startFresh}
              style={{ fontSize: 14, color: "var(--color-muted)", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", padding: isMobile ? "7px 0" : undefined, whiteSpace: "nowrap" }}>
              {isMobile ? "Fresh" : "Start fresh"}
            </button>
          </div>
        </div>
      )}

      {/* The hero photo ships in sizes matched to each screen (public/images/hero:
          WebP at 828/1280/1920/2560 wide, a 1920 JPEG fallback), cropped from the
          original to the band the hero shows, so the whole roofline and both garage
          doors stay in frame on a phone and a laptop. */}
      <section data-hides-ask-mae style={{ position: "relative", minHeight: isMobile ? 286 : 440, maxHeight: isMobile ? undefined : "58vh", overflow: "visible", zIndex: 4, background: "var(--color-ink)" }}>
        <picture>
          <source
            type="image/webp"
            srcSet="/images/hero/home-hero-828.webp 828w, /images/hero/home-hero-1280.webp 1280w, /images/hero/home-hero-1920.webp 1920w, /images/hero/home-hero-2560.webp 2560w"
            sizes="100vw"
          />
          <img
            src="/images/hero/home-hero-1920.jpg"
            alt="A two-story gray house with a three-car garage at dusk"
            fetchPriority="high"
            decoding="async"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 36%" }}
          />
        </picture>
        <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
          {/* A warm umber scrim from the left, where the type sits, and a soft fade into the linen page. */}
          <div style={{ position: "absolute", inset: 0, background: isMobile ? "var(--hero-scrim-mobile)" : "var(--hero-scrim)" }} />
          <div style={{ position: "absolute", inset: 0, background: "var(--hero-fade)" }} />
        </div>

        <div style={{ position: "relative", zIndex: 1, maxWidth: 1120, width: "100%", boxSizing: "border-box", margin: "0 auto", padding: isMobile ? "16px 14px 14px" : "40px 36px 28px", minHeight: isMobile ? 286 : 440, maxHeight: isMobile ? undefined : "58vh", display: "flex", flexDirection: "column", justifyContent: isMobile ? "flex-start" : "center", overflow: "visible" }}>
          <div style={{ maxWidth: 740, width: "100%", minWidth: 0 }}>
            <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 700, color: heroEyebrow, textTransform: "uppercase", letterSpacing: "0.11em", fontFamily: pageFont }}>OpenHomesAI</p>
            <h1 style={{ fontSize: "var(--text-display)", fontWeight: 700, color: heroInk, margin: "0 0 7px", letterSpacing: "-0.015em", lineHeight: 1.06, textShadow: "var(--shadow-text-on-photo)", fontFamily: pageFont, overflowWrap: "anywhere", wordBreak: "normal", maxWidth: isMobile ? "100%" : 720, textWrap: "balance" }}>
              Your AI home search agent.
            </h1>
            <p style={{ fontSize: isMobile ? 14 : 16, color: heroMuted, margin: isMobile ? "0 0 11px" : "0 0 16px", lineHeight: 1.45, maxWidth: 560, overflowWrap: "break-word", fontFamily: pageFont, textShadow: "var(--shadow-text-on-photo)" }}>
              Tell Mae what you want. She reads every new listing in your towns and tells you which ones are worth your time.
            </p>

            <div style={{ maxWidth: isMobile ? 362 : 760, width: "100%", position: "relative", minWidth: 0, boxSizing: "border-box" }}>
                <div style={{ position: "relative", width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box", background: "var(--color-surface)", borderRadius: "var(--radius-lg)", boxShadow: "var(--shadow-menu)", border: "1px solid var(--color-glass-dim)", overflow: "hidden" }}>
                <div style={{ display: "flex", flexDirection: "row", alignItems: "stretch", width: "100%", maxWidth: "100%", boxSizing: "border-box", minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
                    <span style={{ padding: isMobile ? "0 11px" : "0 16px", display: "flex", alignItems: "center", color: "var(--color-muted-3)", fontSize: 20, flexShrink: 0, fontFamily: pageFont }}>⌕</span>
                    <input
                      className="hero-search-input"
                      style={{ flex: 1, minWidth: 0, padding: isMobile ? "14px 8px 14px 0" : "17px 8px 17px 0", fontSize: isMobile ? 16 : 16, border: "none", outline: "none", color: "var(--color-ink)", background: "transparent", fontFamily: pageFont }}
                      placeholder={isMobile ? "Town, ZIP, or needs" : "Try: 3 bed home near Morristown under $900K with privacy"}
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setAutocompleteOpen(true);
                    }}
                    onFocus={event => {
                      setAutocompleteOpen(true);
                      if (isMobile) {
                        const target = event.currentTarget;
                        window.setTimeout(() => target.scrollIntoView({ block: "center", behavior: "smooth" }), 80);
                      }
                    }}
                    onBlur={() => window.setTimeout(() => setAutocompleteOpen(false), 120)}
                    onKeyDown={handleSearchKeyDown}
                    role="combobox"
                    aria-expanded={autocompleteOpen && activeDropdownCount > 0}
                    aria-controls="homepage-search-suggestions"
                    aria-autocomplete="list"
                  />
                  </div>
                  <button onClick={() => handleSearch()}
                    style={{ width: isMobile ? 54 : undefined, minHeight: isMobile ? 48 : undefined, borderRadius: 0, padding: isMobile ? "0" : "0 30px", background: isMobile ? "var(--color-brand)" : "linear-gradient(135deg, var(--color-brand-dark), var(--color-brand))", color: "var(--color-surface)", border: "none", fontSize: isMobile ? 14 : 16, fontWeight: 700, cursor: "pointer", fontFamily: pageFont, flexShrink: 0 }}>
                    {isMobile ? "Go" : "Search"}
                  </button>
                </div>
                </div>
              {autocompleteOpen && activeDropdownCount > 0 && (
                <div
                  id="homepage-search-suggestions"
                  role="listbox"
                  style={{
                    position: "absolute",
                    top: "calc(100% + 8px)",
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    maxHeight: isMobile ? "min(52dvh, calc(var(--app-dynamic-viewport-height) - 120px), 330px)" : 390,
                    overflowY: "auto",
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-md)",
                    background: "var(--color-surface)",
                    boxShadow: "var(--shadow-menu)",
                    padding: 6,
                    fontFamily: pageFont,
                  }}
                >
                  {showAutocompleteResults ? autocompleteSuggestions.map((suggestion, index) => {
                    const active = index === activeSuggestionIndex;
                    return (
                      <button
                        key={`${suggestion.category}-${suggestion.query}`}
                        type="button"
                        role="option"
                        aria-selected={active}
                        onMouseEnter={() => setActiveSuggestionIndex(index)}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          selectAutocompleteSuggestion(suggestion);
                        }}
                        style={{
                          width: "100%",
                          border: "none",
                          borderRadius: "var(--radius-sm)",
                          background: active ? "var(--color-advisor-bg)" : "transparent",
                          color: "var(--color-ink)",
                          cursor: "pointer",
                          display: "grid",
                          gridTemplateColumns: "minmax(0, 1fr) auto",
                          gap: 12,
                          alignItems: "center",
                          padding: isMobile ? "12px 10px" : "11px 12px",
                          minHeight: isMobile ? 50 : 44,
                          textAlign: "left",
                          fontFamily: pageFont,
                        }}
                      >
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: "block", fontSize: isMobile ? 14 : 16, fontWeight: 700, color: "var(--color-ink-soft)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {suggestion.label}
                          </span>
                          <span style={{ display: "block", marginTop: 2, fontSize: 12, color: "var(--color-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {suggestion.detail}
                          </span>
                        </span>
                        <span style={{ border: "1px solid var(--color-border-2)", background: active ? "var(--color-surface)" : "var(--color-surface-muted)", borderRadius: "var(--radius-full)", padding: "4px 8px", fontSize: 12, fontWeight: 700, color: "var(--color-ink)", textTransform: "capitalize", whiteSpace: "nowrap" }}>
                          {suggestion.category === "suggested" ? "idea" : suggestion.category === "custom" ? "search" : suggestion.category === "didyoumean" ? "suggestion" : suggestion.category}
                        </span>
                      </button>
                    );
                  }) : (
                    <div style={{ display: "grid", gap: 3 }}>
                      {(["Action", "Resume", "Saved", "AI"] as const).map(section => {
                        const sectionItems = searchHubItems.filter(item => item.section === section);
                        if (!sectionItems.length) return null;
                        return (
                          <div key={section} style={{ padding: "4px 0" }}>
                            <p style={{ margin: "5px 10px 4px", color: "var(--color-muted-3)", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                              {section === "Action" ? "Start" : section}
                            </p>
                            {sectionItems.map(item => {
                              const globalIndex = searchHubItems.findIndex(candidate => candidate.id === item.id);
                              const active = globalIndex === activeSuggestionIndex;
                              return (
                                <button
                                  key={item.id}
                                  type="button"
                                  role="option"
                                  aria-selected={active}
                                  onMouseEnter={() => setActiveSuggestionIndex(globalIndex)}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    setAutocompleteOpen(false);
                                    item.onSelect();
                                  }}
                                  style={{
                                    width: "100%",
                                    border: "none",
                                    borderRadius: "var(--radius-sm)",
                                    background: active ? "var(--color-advisor-bg)" : "transparent",
                                    cursor: "pointer",
                                    display: "grid",
                                    gridTemplateColumns: "minmax(0, 1fr) auto",
                                    gap: 12,
                                    alignItems: "center",
                                    padding: isMobile ? "12px 10px" : "11px 12px",
                                    minHeight: isMobile ? 50 : undefined,
                                    textAlign: "left",
                                    fontFamily: pageFont,
                                  }}
                                >
                                  <span style={{ minWidth: 0 }}>
                                    <span style={{ display: "block", fontSize: isMobile ? 14 : 16, fontWeight: 700, color: "var(--color-ink-soft)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                      {item.label}
                                    </span>
                                    <span style={{ display: "block", marginTop: 2, fontSize: 12, color: "var(--color-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                      {item.detail}
                                    </span>
                                  </span>
                                  <span style={{ border: "1px solid var(--color-border-2)", background: active ? "var(--color-surface)" : "var(--color-surface-muted)", borderRadius: "var(--radius-full)", padding: "4px 8px", fontSize: 12, fontWeight: 700, color: "var(--color-ink)", whiteSpace: "nowrap" }}>
                                    {item.kind}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
                <div style={{ marginTop: isMobile ? 10 : 12 }}>
                  <div style={{ display: "flex", flexDirection: isMobile ? "row" : "row", alignItems: "center", justifyContent: "space-between", gap: isMobile ? 8 : 10, flexWrap: "wrap", marginBottom: 7, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 12, color: heroMuted, fontWeight: 700, fontFamily: pageFont }}>
                      {recommendationHeading}
                    </p>
                    {!isMobile && !user && locationStatus === "idle" && (
                      <button
                      type="button"
                      onClick={requestBetterLocation}
                      style={{ ...heroChip, borderRadius: "var(--radius-full)", minHeight: 44, padding: "0 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: pageFont }}
                    >
                      Use my location
                    </button>
                  )}
                  {locationStatus === "requesting" && <span style={{ fontSize: 12, color: heroMuted, fontWeight: 700 }}>Checking area…</span>}
                  {(locationStatus === "denied" || locationStatus === "unsupported") && <span style={{ fontSize: 12, color: heroMuted, fontWeight: 600 }}>Using popular North Jersey searches</span>}
                </div>
                  <div style={{ display: "flex", flexWrap: "nowrap", gap: 7, minWidth: 0, maxWidth: "100%", overflowX: isMobile ? "auto" : "visible", WebkitOverflowScrolling: "touch", paddingBottom: isMobile ? 3 : 0, scrollbarWidth: "none" }}>
                  {(isMobile ? mobileSearchIdeas : recommendations).map((item: HomepageSearchRecommendation | { label: string; onSelect: () => void }) => (
                    <button
                      key={"query" in item ? `${item.source}-${item.query}` : item.label}
                      onClick={() => "query" in item ? handleSearch(item.query) : item.onSelect()}
                      className="hero-chip"
                      title={process.env.NODE_ENV === "development" && "reason" in item ? item.reason : undefined}
                      style={{
                        ...heroChip,
                        padding: isMobile ? "0 12px" : "0 16px",
                        minHeight: 44,
                      borderRadius: "var(--radius-full)",
                      backdropFilter: "blur(10px)",
                      cursor: "pointer",
                      fontFamily: pageFont,
                      fontSize: isMobile ? 12 : 14,
                      fontWeight: 600,
                      lineHeight: 1.2,
                        maxWidth: isMobile ? "none" : "100%",
                        flex: isMobile ? "0 0 auto" : undefined,
                        overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    >
                      {item.label}
                    </button>
                  ))}
                  </div>
                </div>
            </div>

          </div>
        </div>
      </section>

      <section style={{ background: "var(--color-bg-subtle)", borderTop: "1px solid var(--color-border)", position: "relative", zIndex: 1 }}>
        <div style={{ maxWidth: 1120, width: "100%", boxSizing: "border-box", margin: "0 auto", padding: isMobile ? "18px 14px 8px" : "26px 36px 12px", overflow: "hidden" }}>
          <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-end", justifyContent: "space-between", gap: isMobile ? 10 : 14, marginBottom: isMobile ? 11 : 14, flexWrap: "nowrap", minWidth: 0 }}>
            <div style={{ minWidth: 0, maxWidth: "100%", flex: "1 1 auto" }}>
              <p className="oh-eyebrow" style={{ margin: "0 0 6px" }}>{starterListingsContext?.nearby ? "Nearby homes" : anchorTown ? "Near your search" : "Explore"}</p>
              <h2 style={{ margin: 0, color: "var(--color-ink)", fontSize: isMobile ? 20 : 28, fontWeight: 700, lineHeight: 1.12 }}>
                {starterListingsContext?.nearby && starterListingsContext.town
                  ? `Homes near ${anchorTown || starterListingsContext.town}`
                  : "Homes to start with"}
              </h2>
              <p style={{ margin: "5px 0 0", color: "var(--color-muted)", fontSize: isMobile ? 14 : 14, lineHeight: 1.35, overflowWrap: "break-word" }}>
                {starterListingsContext?.nearby
                  ? isMobile ? `Nearby options while ${anchorTown || starterListingsContext.town} fills in.` : `Available homes near ${anchorTown || starterListingsContext.town}. We’ll show nearby options while this market is being populated.`
                  : anchorTown
                    ? isMobile ? `Available ${anchorTown} listings.` : `Available ${anchorTown} listings from the current app data.`
                    : "A quick look at available homes from the current app data."}
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleSearch(anchorTown ? `${anchorTown}, NJ` : "North Jersey homes")}
              style={{ border: "1px solid var(--color-border)", background: "var(--color-surface)", color: "var(--color-ink)", borderRadius: "var(--radius-full)", minHeight: 44, padding: "0 14px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", width: "auto", boxSizing: "border-box", flexShrink: 0, whiteSpace: "nowrap" }}
            >
              {isMobile ? "See all" : "See more homes"}
            </button>
          </div>

          {starterListingsLoading && starterListings.length === 0 ? (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 12 }}>
              {Array.from({ length: isMobile ? 2 : 3 }).map((_, index) => (
                <div key={index} style={{ height: 255, border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", background: "var(--color-surface)", boxShadow: "var(--shadow-soft)" }} />
              ))}
            </div>
          ) : starterListings.length > 0 ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? `repeat(${starterListings.length}, minmax(282px, 86vw))` : "repeat(3, minmax(0, 1fr))",
                gap: 12,
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                overflowX: isMobile ? "auto" : "visible",
                overflowY: "hidden",
                WebkitOverflowScrolling: "touch",
                scrollSnapType: isMobile ? "x mandatory" : undefined,
                paddingBottom: isMobile ? 10 : 0,
              }}
            >
              {starterListings.slice(0, isMobile ? 4 : 6).map(listing => (
                <HomepageListingCard key={listing.id} listing={listing} onOpen={(home) => router.push(`/listing/${home.id}`)} />
              ))}
            </div>
          ) : (
            <div style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", background: "var(--color-surface)", padding: 16, color: "var(--color-muted)", fontSize: 14, display: "grid", gap: 11 }}>
              <p style={{ margin: 0, color: "var(--color-ink)", fontSize: 16, fontWeight: 700 }}>No starter homes are available right now.</p>
              <p style={{ margin: 0, lineHeight: 1.45, maxWidth: "100%", overflowWrap: "anywhere", wordBreak: "normal" }}>Try a town search above, or ask Mae where to look next.</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button type="button" onClick={() => handleSearch(anchorTown ? `${anchorTown}, NJ` : "Morristown, NJ")} style={{ border: "1px solid var(--color-brand)", background: "var(--color-brand)", color: "var(--color-surface)", borderRadius: "var(--radius-sm)", minHeight: 44, padding: "0 11px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Search nearby</button>
                <button type="button" onClick={() => router.push(buildAdvisorUrl({ surface: "home", mode: "refine_search" }))} style={{ border: "1px solid var(--color-border)", background: "var(--color-surface)", color: "var(--color-ink)", borderRadius: "var(--radius-sm)", minHeight: 44, padding: "0 11px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Ask Mae</button>
              </div>
            </div>
          )}
          {isMobile && (
            <div data-hides-ask-mae style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", marginTop: 12, border: "1px solid var(--color-border)", background: "var(--color-surface)", borderRadius: "var(--radius-lg)", padding: 12, display: "grid", gridTemplateColumns: "auto minmax(0, 1fr) auto", gap: 10, alignItems: "center", boxShadow: "var(--shadow-raised)", overflow: "hidden" }}>
              <AdvisorAvatar size="sm" />
              <div style={{ minWidth: 0 }}>
                <p style={{ margin: "0 0 3px", color: "var(--color-ink)", fontSize: 12, fontWeight: 700 }}>Ask Mae</p>
                <p style={{ margin: 0, color: "var(--color-ink)", fontSize: 14, lineHeight: 1.25, fontWeight: 700 }}>What should I look at next?</p>
              </div>
              <button type="button" onClick={() => router.push(buildAdvisorUrl({ surface: "home", mode: "refine_search" }))} style={{ minHeight: 44, border: "1px solid var(--color-brand)", background: "var(--color-brand)", color: "var(--color-surface)", borderRadius: "var(--radius-full)", padding: "0 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                Open
              </button>
            </div>
          )}
        </div>
      </section>

      <section style={{ background: "var(--color-bg-subtle)", borderTop: "1px solid var(--color-border)", position: "relative", zIndex: 1 }}>
        <div style={{ maxWidth: 1120, width: "100%", boxSizing: "border-box", margin: "0 auto", padding: isMobile ? "18px 14px 16px" : "22px 36px 22px", overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 0.9fr) minmax(0, 1.5fr)", gap: isMobile ? 14 : 20, alignItems: "center" }}>
            <div style={{ minWidth: 0 }}>
              <p className="oh-eyebrow" style={{ margin: "0 0 7px" }}>Why OpenHomesAI</p>
              <h2 style={{ margin: 0, color: "var(--color-ink)", fontSize: isMobile ? 20 : 28, lineHeight: 1.12, fontWeight: 700, letterSpacing: 0 }}>
                Search smarter, then let Mae help.
              </h2>
              <p style={{ margin: "8px 0 0", color: "var(--color-muted)", fontSize: isMobile ? 14 : 14, lineHeight: 1.45, maxWidth: 520 }}>
                OpenHomesAI helps you search, compare tradeoffs, remember preferences, and watch for better matches without turning the homepage into a tutorial.
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 9, marginTop: isMobile ? 12 : 14 }}>
                <button
                  type="button"
                  onClick={() => router.push(buildAdvisorUrl({ surface: "home", mode: "refine_search" }))}
                  style={{ border: "1px solid var(--color-brand)", background: "var(--color-brand)", color: "var(--color-surface)", borderRadius: "var(--radius-sm)", minHeight: 44, padding: "0 11px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
                >
                  Ask Mae
                </button>
                <button
                  type="button"
                  onClick={() => router.push("/about")}
                  style={{ border: "1px solid var(--color-border)", background: "var(--color-surface)", color: "var(--color-ink)", borderRadius: "var(--radius-sm)", minHeight: 44, padding: "0 11px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
                >
                  How it works
                </button>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 10, minWidth: 0 }}>
              {HOME_ADVISOR_STEPS.slice(0, 3).map(item => (
                <div key={item.step} style={{ border: "1px solid var(--color-border)", background: "var(--color-surface)", borderRadius: "var(--radius-sm)", padding: isMobile ? "11px 12px" : "12px 13px", minWidth: 0, boxShadow: "var(--shadow-raised)" }}>
                  <h3 style={{ margin: "0 0 5px", color: "var(--color-ink)", fontSize: isMobile ? 16 : 14, lineHeight: 1.2, fontWeight: 700 }}>
                    {item.title}
                  </h3>
                  <p style={{ margin: 0, color: "var(--color-muted)", fontSize: 12, lineHeight: 1.4 }}>
                    {item.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section style={{ background: "linear-gradient(180deg, var(--color-bg-subtle), var(--color-bg))", borderTop: "1px solid var(--color-border)" }}>
        <div style={{ maxWidth: 1120, width: "100%", boxSizing: "border-box", margin: "0 auto", padding: isMobile ? "24px 18px 38px" : "30px 36px 52px", overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "280px minmax(0, 1fr)", gap: isMobile ? 20 : 24, alignItems: "start" }}>
            <aside style={{ minWidth: 0 }}>
              <p className="oh-eyebrow" style={{ margin: "0 0 8px" }}>Nearby markets</p>
              <h2 style={{ fontSize: isMobile ? 20 : 28, fontWeight: 700, color: "var(--color-ink)", margin: "0 0 9px", lineHeight: 1.14, letterSpacing: 0, overflowWrap: "anywhere", maxWidth: isMobile ? 330 : undefined }}>
                Explore towns OpenHomesAI can watch.
              </h2>
              <p style={{ fontSize: 14, color: "var(--color-muted)", margin: "0 0 16px", lineHeight: 1.5, overflowWrap: "break-word" }}>
                Start with a nearby area, then add privacy, commute, school, layout, or budget tradeoffs.
              </p>

              <div style={{ display: "grid", gap: 6 }}>
                {visibleMarkets.map((group, index) => {
                  const active = index === selectedMarket;
                  const count = group.cities.reduce((sum, city) => sum + (cityCounts[city] || 0), 0);
                  return (
                    <button key={group.label} onClick={() => setSelectedMarket(index)}
                      style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(0, 1fr) auto", gap: 12, alignItems: "center", textAlign: "left", border: active ? "1px solid var(--color-border)" : "1px solid transparent", borderRadius: "var(--radius-sm)", padding: "10px 12px", background: active ? "var(--color-ink)" : "transparent", color: active ? "var(--color-surface)" : "var(--color-ink-soft)", cursor: "pointer", fontFamily: "inherit", boxShadow: active ? "var(--shadow-menu)" : "none", width: "100%", minWidth: 0, boxSizing: "border-box" }}>
                      <span>
                        <span style={{ display: "block", fontSize: 14, fontWeight: 700 }}>{group.label}</span>
                        <span style={{ display: "block", marginTop: 2, fontSize: 12, color: active ? "var(--color-glass-soft)" : "var(--color-muted-2)" }}>{group.cities.length} towns</span>
                      </span>
                      <span style={{ display: isMobile ? "none" : "inline", fontSize: 12, fontWeight: 700, color: active ? "var(--color-glass-soft)" : "var(--color-muted)" }}>
                        {count ? count.toLocaleString() : "Search"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </aside>

            <div className="oh-surface" style={{ overflow: "hidden" }}>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1fr) 210px", gap: 16, padding: isMobile ? "16px" : "18px 20px", borderBottom: "1px solid var(--color-border)", background: "linear-gradient(135deg, var(--color-surface-muted), var(--color-advisor-bg))" }}>
                <div>
                  <p style={{ margin: "0 0 5px", fontSize: 20, fontWeight: 700, color: "var(--color-ink)", letterSpacing: 0 }}>{activeMarket.label}</p>
                  <p style={{ margin: 0, fontSize: 14, color: "var(--color-muted)", lineHeight: 1.45 }}>{activeMarket.description}</p>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <p style={{ margin: 0, fontSize: 12, color: "var(--color-muted-3)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>Active</p>
                    <p style={{ margin: "3px 0 0", fontSize: 20, color: "var(--color-ink)", fontWeight: 700 }}>{activeMarketTotal ? activeMarketTotal.toLocaleString() : "Live"}</p>
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: 12, color: "var(--color-muted-3)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>Mode</p>
                    <p style={{ margin: "6px 0 0", display: "inline-block", fontSize: 12, color: "var(--color-ink)", fontWeight: 700, background: "var(--color-surface-muted)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-full)", padding: "3px 8px" }}>Watchable</p>
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1fr) 270px" }}>
                <div style={{ padding: isMobile ? "6px 16px 12px" : "8px 20px 16px" }}>
                  {activeMarket.cities.map(city => (
                    <button key={city} onClick={() => handleSearch(`${city}, NJ`)}
                      style={{ width: "100%", display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 14, alignItems: "center", background: "transparent", border: "none", borderBottom: "1px solid var(--color-border-3)", padding: "12px 0", cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
                      <span>
                        <span style={{ display: "block", fontSize: 16, color: "var(--color-ink-soft)", fontWeight: 700 }}>{city}</span>
                        <span style={{ display: "block", marginTop: 3, fontSize: 12, color: "var(--color-muted-3)" }}>View listings or tell Mae what matters</span>
                      </span>
                      <span style={{ fontSize: 12, color: cityCounts[city] ? "var(--color-ink)" : "var(--color-muted-2)", fontWeight: 700 }}>
                        {cityCounts[city] ? `${cityCounts[city].toLocaleString()} active` : "Search"} ›
                      </span>
                    </button>
                  ))}
                </div>

                <div style={{ borderLeft: isMobile ? "none" : "1px solid var(--color-border-2)", borderTop: isMobile ? "1px solid var(--color-border-2)" : "none", background: "var(--color-bg)", padding: isMobile ? "16px" : "16px" }}>
                  <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--color-ink)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>{activeMarket.key === visibleMarkets[0]?.key ? "Relevant starter searches" : "Starter searches"}</p>
                  <div style={{ display: "grid", gap: 8 }}>
                    {activeMarket.searches.map(search => (
                      <button key={search} onClick={() => handleSearch(search)}
                        style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", background: "var(--color-surface)", padding: "9px 10px", textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}>
                        <span style={{ display: "block", fontSize: 14, color: "var(--color-ink-soft)", fontWeight: 700 }}>{search}</span>
                        <span style={{ display: "block", marginTop: 3, fontSize: 12, color: "var(--color-muted-3)" }}>Run this search ›</span>
                      </button>
                    ))}
                  </div>
                  <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--color-border)" }}>
                    <p style={{ margin: "0 0 4px", fontSize: 14, color: "var(--color-ink-soft)", fontWeight: 700 }}>Tell Mae</p>
                    <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--color-muted)", lineHeight: 1.55 }}>Tell it what to watch for: privacy, larger lots, better schools, commute, updated kitchens, or avoiding busy roads.</p>
                    <button type="button" onClick={() => router.push(buildAdvisorUrl({ surface: "home", mode: "refine_search" }))} style={{ border: "1px solid var(--color-border)", background: "var(--color-surface)", color: "var(--color-ink)", borderRadius: "var(--radius-sm)", minHeight: 44, padding: "0 10px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                      Add preferences
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14, marginTop: 18 }}>
            <div style={{ border: "1px solid var(--color-border)", background: "var(--color-surface)", borderRadius: "var(--radius-md)", padding: isMobile ? 16 : 18 }}>
              <p className="oh-eyebrow" style={{ margin: "0 0 8px", display: "flex", alignItems: "center", gap: 7 }}><AdvisorAvatar size="xs" /><MayWordmark /></p>
              <h3 style={{ margin: "0 0 7px", color: "var(--color-ink)", fontSize: 20, fontWeight: 700 }}>Keep teaching what matters.</h3>
              <p style={{ margin: "0 0 12px", color: "var(--color-muted)", fontSize: 14, lineHeight: 1.5 }}>Save homes, mark tradeoffs, and OpenHomesAI will watch for stronger matches and useful near-misses.</p>
              <button type="button" onClick={() => router.push(buildAdvisorUrl({ surface: "home", mode: "refine_search" }))} style={{ border: "1px solid var(--color-brand)", background: "var(--color-brand)", color: "var(--color-surface)", borderRadius: "var(--radius-sm)", minHeight: 44, padding: "0 12px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                Open Mae
              </button>
            </div>
            <div style={{ border: "1px solid var(--color-border)", background: "var(--color-surface)", borderRadius: "var(--radius-md)", padding: isMobile ? 16 : 18 }}>
              <p className="oh-eyebrow" style={{ margin: "0 0 8px" }}>Real agent</p>
              <h3 style={{ margin: "0 0 7px", color: "var(--color-ink)", fontSize: 20, fontWeight: 700 }}>Ready when the right home appears.</h3>
              <p style={{ margin: "0 0 12px", color: "var(--color-muted)", fontSize: 14, lineHeight: 1.5 }}>Share a home, your saved preferences, and Mae's questions with a real agent when you are ready to tour.</p>
              <button type="button" onClick={() => router.push(buildAdvisorUrl({ surface: "home", mode: "refine_search" }))} style={{ border: "1px solid var(--color-border)", background: "var(--color-surface)", color: "var(--color-ink)", borderRadius: "var(--radius-sm)", minHeight: 44, padding: "0 12px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                Prepare realtor context
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
